import axios from "axios";
import { API_URL } from "../constants";
import { TaskItem } from "./cabinet.service";

/**
 * The lists behind the «Užduotys» queue cards — erp-backend
 * GET /:companyId/cabinet/queue/sign and /queue/review. Both cover every company
 * the person is an enabled member of; `:companyId` only says which company's page asks.
 */

/** Where a document comes from: work documents of the person's job, their own, or their team's requests. */
export type SignGroup = "work" | "mine" | "team";

/** The leave request behind a team document. */
export interface LeaveFacts {
  employeeName: string | null;
  leaveType: string;
  duration: string;
  /** YYYY-MM-DD */
  startDate: string;
  endDate: string;
  /** HH:mm, set for hourly leave. */
  startTime: string | null;
  endTime: string | null;
}

/** One document waiting for the person's signature, ready to be signed in the app. */
export interface SignQueueItem extends TaskItem {
  fileId: string;
  signType: "Click" | "Dokobit";
  group: SignGroup;
  /** Who created or submitted the document (shown as «Pateikė»). */
  ownerName: string | null;
  customerNames: string[];
  /** Only for team documents. */
  leave: LeaveFacts | null;
}

/** A work document that waits for a paper signature: listed, never signed in the app. */
export interface PaperItem {
  id: string;
  fileId: string;
  title: string;
  companyId: string;
  companyName: string;
  since: string;
  link: string;
}

export interface SignQueue {
  /** Oldest first. */
  items: SignQueueItem[];
  paper: PaperItem[];
}

/** A document's signing: nobody asked to sign yet, still waiting for someone, or fully signed. */
export type SignStatus = "not_initiate" | "pending" | "signed";

export interface ReviewQueueItem {
  /** The review row (FilesToReviewUsers.id), which is what «mark as reviewed» takes. */
  id: string;
  fileId: string;
  title: string;
  companyId: string;
  companyName: string;
  since: string;
  link: string;
  signStatus: SignStatus;
  /** Where the list continues after this row (`after`). */
  cursor: string;
}

export interface ReviewQueueAnswer {
  /** Newest first. */
  items: ReviewQueueItem[];
  /** All rows under the same `company` and `q`. */
  total: number;
  /** Null when the request used `after`. */
  page: number | null;
  pageSize: number;
  /** Every company that has documents to review: the «Įmonė» choices, not narrowed by `company` or `q`. */
  companies: { companyId: string; companyName: string }[];
}

/** `page` (the list) and `after` (the sheet) are alternatives: sending both is a 400. */
export interface ReviewQueueQuery {
  company?: string;
  /** Searched in the file name and title; the server ignores anything shorter than 2 characters. */
  q?: string;
  pageSize?: number;
  page?: number;
  after?: string;
}

export async function fetchSignQueue(companyId: string): Promise<SignQueue> {
  const res = await axios.get<SignQueue>(`${API_URL}/${companyId}/cabinet/queue/sign`);
  // A failed decoration comes back as null or [] rather than failing the list.
  const items = res.data.items.map((item) => ({ ...item, customerNames: item.customerNames ?? [] }));
  return { ...res.data, items };
}

export async function fetchReviewQueue(companyId: string, query: ReviewQueueQuery): Promise<ReviewQueueAnswer> {
  const res = await axios.get<ReviewQueueAnswer>(`${API_URL}/${companyId}/cabinet/queue/review`, {
    params: query,
  });
  return res.data;
}
